/**
 * Pony V7 Family Graph V2
 *
 * Controls for PonyV7 ecosystem (based on AuraFlow architecture).
 * Meta contains only dynamic props - static props defined in components.
 *
 * Note: Pony V7 works best with 40+ steps.
 * No negative prompts, samplers, or CLIP skip.
 * Supports LoRA resources.
 */

import { DataGraph } from '~/libs/data-graph/data-graph';
import type { GenerationCtx } from './context';
import {
  aspectRatioNode,
  createCheckpointGraph,
  createResourcesGraph,
  promptGraph,
  seedNode,
  sliderNode,
  snippetsGraph,
  triggerWordsGraph,
} from './common';
import { sdxlAspectRatioBuckets } from '~/shared/constants/generation.constants';

// =============================================================================
// Constants
// =============================================================================

/** Pony V7 model version ID */
const ponyV7VersionId = 2152373;

// =============================================================================
// Guidance Presets
// =============================================================================

/** Pony V7 guidance presets */
const ponyV7GuidancePresets = [
  { label: 'Low', value: 2 },
  { label: 'Balanced', value: 3.5 },
  { label: 'High', value: 7 },
];

// =============================================================================
// Pony V7 Graph V2
// =============================================================================

/**
 * Pony V7 family controls.
 *
 * Meta only contains dynamic props - static props like label are in components.
 * Note: Pony V7 doesn't use negative prompts, samplers, or CLIP skip.
 * This model works best with 40+ steps.
 */
export const ponyV7Graph = new DataGraph<{ ecosystem: string; workflow: string }, GenerationCtx>()
  // Merge checkpoint graph
  .merge(
    () =>
      createCheckpointGraph({
        defaultModelId: ponyV7VersionId,
      }),
    []
  )
  .merge(createResourcesGraph())
  .merge(triggerWordsGraph)
  .merge(snippetsGraph)
  .merge(promptGraph)
  .node('aspectRatio', aspectRatioNode({ options: sdxlAspectRatioBuckets, defaultValue: '1:1' }))
  .node(
    'cfgScale',
    sliderNode({ min: 2, max: 20, defaultValue: 3.5, step: 0.5, presets: ponyV7GuidancePresets })
  )
  .node(
    'steps',
    sliderNode({ min: 20, max: 50, defaultValue: 40 }) // Pony V7 works best with 40+ steps
  )
  .node('seed', seedNode());
